import { Fragment, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';

// P0-2 (claude/world-class-readiness-architecture-review.md, 2026-08-30): the first real
// per-impression "why didn't this monetize" surface this dashboard has ever had, over the new
// auction_events table (config-backend/migrations/016_auction_events.sql) -- every runtime's own
// bidder, inbound oRTB, VAST, CTV) writes exactly one durable row per real auction, via
// auction-service's shared handleAuctionRequest. Keyset-paginated (see the route's own header
// comment for why -- this deliberately avoids repeating the pre-existing Audit Log page's
// fixed-200-row/no-real-pagination limitation, flagged as P1-4 in the same review).
const INTEGRATION_TYPES = [
  { value: '', label: 'All runtimes' },
  { value: 'own', label: 'Own bidder (Prebid.js)' },
  { value: 'inbound', label: 'Inbound oRTB' },
  { value: 'vast', label: 'VAST' },
  { value: 'ctv', label: 'CTV' },
];

// P1 finding #10 (claude/p0-p1-readiness-review-2026-08-30-status.md): per-partner outcome/latency
// for one auction, over the new auction_partner_results table -- lazily fetched only when a row
// is expanded (see loadPartnerResults below), not eagerly joined into the main list query, so a
// migration not yet applied to production can only ever affect this one panel, never the rest of
// this page. Labels/colors deliberately name the actual failure mode, not just "no bid" -- this
// is the first place in this codebase that can show a timeout separately from an error separately
// from a genuine no-bid.
const OUTCOME_LABELS = {
  bid_won: 'Won',
  bid_lost: 'Lost to a higher bid',
  floor_rejected: 'Bid below floor',
  no_bid: 'No bid',
  timeout: 'Timed out',
  network_error: 'Network error',
  http_error: 'HTTP error',
  missing_credentials: 'Missing credentials',
  // Workstream 1 (generic oRTB connector): a 2xx response whose body isn't valid JSON -- kept
  // distinct from network_error/http_error since the call itself succeeded at the transport
  // level (see runAuction.js's postWithTimeout for why this couldn't just be silently treated as
  // a no-bid the way an empty 204 body is).
  invalid_response: 'Invalid response body',
};
// Reuses this dashboard's existing .status-badge system (theme.css) rather than inventing a new
// one -- 'active'/green for a real win, 'error'/red for the failure modes an operator should
// actually act on (timeout/network/HTTP error), 'paused'/amber for a real but fixable
// misconfiguration (missing credentials on an enabled partner), 'neutral'/grey for everything
// else that's a normal, expected auction outcome (lost, floor-rejected, genuine no-bid).
const OUTCOME_BADGE = {
  bid_won: 'active',
  bid_lost: 'neutral',
  floor_rejected: 'neutral',
  no_bid: 'neutral',
  timeout: 'error',
  network_error: 'error',
  http_error: 'error',
  missing_credentials: 'paused',
  invalid_response: 'error',
};

export default function AuctionLogPage() {
  const [rows, setRows] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [publisherId, setPublisherId] = useState('');
  const [integrationType, setIntegrationType] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [partnerResultsById, setPartnerResultsById] = useState({}); // id -> {loading, error, rows}

  async function toggleExpanded(id) {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (partnerResultsById[id]) return; // already fetched (or in flight) for this row
    setPartnerResultsById((prev) => ({ ...prev, [id]: { loading: true, error: null, rows: null } }));
    try {
      const result = await api.auctionEventPartnerResults(id);
      setPartnerResultsById((prev) => ({ ...prev, [id]: { loading: false, error: null, rows: result.rows } }));
    } catch (err) {
      // A migration not yet applied to production surfaces here as a plain fetch error -- shown
      // inline in this one panel only, never blocking the rest of the Auction Log page.
      const message = err instanceof ApiError ? err.message : 'Could not load partner diagnostics.';
      setPartnerResultsById((prev) => ({ ...prev, [id]: { loading: false, error: message, rows: null } }));
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const result = await api.auctionEvents({
        publisherId: publisherId || undefined,
        integrationType: integrationType || undefined,
        limit: 50,
      });
      setRows(result.rows);
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the auction log.');
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const result = await api.auctionEvents({
        publisherId: publisherId || undefined,
        integrationType: integrationType || undefined,
        limit: 50,
        before: nextCursor,
      });
      setRows((prev) => [...(prev || []), ...result.rows]);
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load more.');
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page">
      <PageHeader
        title="Auction Log"
        subtitle="Every real auction across every runtime, one row per request -- imps, wins, revenue, which config governed it, and why."
      />

      <div className="toolbar">
        <input
          className="toolbar__search"
          placeholder="Filter by publisher ID&hellip;"
          value={publisherId}
          onChange={(e) => setPublisherId(e.target.value)}
          inputMode="numeric"
        />
        <select className="toolbar__search" value={integrationType} onChange={(e) => setIntegrationType(e.target.value)}>
          {INTEGRATION_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <button className="btn btn--ghost" onClick={load}>
          Apply filters
        </button>
      </div>

      {loading && <LoadingState label="Loading auction log" />}
      {!loading && error && <EmptyState title="Couldn't load auction log" message={error} />}
      {!loading && !error && rows && rows.length === 0 && (
        <EmptyState title="No matching auctions" message="Try clearing your filters, or check back after some real traffic runs." />
      )}

      {!loading && !error && rows && rows.length > 0 && (
        <>
          <section className="card card--flush">
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Publisher</th>
                    <th>Runtime</th>
                    <th>Imps / Wins</th>
                    <th>Revenue (net INR)</th>
                    <th>Config source</th>
                    <th>Fill</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <Fragment key={r.id}>
                      <tr className="data-table__row--expandable" onClick={() => toggleExpanded(r.id)}>
                        <td className="cell-muted">{formatDateTime(r.created_at)}</td>
                        <td>
                          {r.publisher_name}
                          <span className="cell-subtitle"> #{r.publisher_id}</span>
                        </td>
                        <td>
                          <code className="code-chip code-chip--action">{r.integration_type}</code>
                        </td>
                        <td>{r.imp_count} / {r.win_count}</td>
                        <td>{Number(r.revenue_net_inr).toFixed(2)}</td>
                        <td className="cell-muted">
                          floor: {r.floor_source || '—'}, demand: {r.demand_partners_source || '—'}
                        </td>
                        <td>{r.house_ad_fill ? <span className="code-chip">house ad</span> : (r.win_count > 0 ? 'real bid' : 'no fill')}</td>
                        <td>
                          <span className={`disclosure${expandedId === r.id ? ' disclosure--open' : ''}`} aria-hidden="true">
                            &#9656;
                          </span>
                        </td>
                      </tr>
                      {expandedId === r.id && (
                        <tr className="data-table__expansion-row">
                          <td colSpan={8}>
                            <PartnerDiagnostics state={partnerResultsById[r.id]} />
                            <pre className="diff-view">{JSON.stringify({
                              idempotencyKey: r.idempotency_key,
                              configVersionId: r.config_version_id,
                              partnersCalled: r.partners_called,
                              partnersResponded: r.partners_responded,
                              partnerWinCounts: r.partner_win_counts,
                              currency: r.currency,
                            }, null, 2)}</pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {nextCursor && (
            <div className="pagination">
              <button className="btn btn--ghost btn--sm" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? 'Loading…' : 'Load older auctions'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// P1 finding #10 (claude/p0-p1-readiness-review-2026-08-30-status.md): the first surface this
// dashboard has ever had that can say WHY a partner didn't monetize a given auction -- timed out,
// errored, genuinely no-bid, floor-rejected, or simply lost -- instead of the pre-existing raw
// partnersCalled/partnersResponded arrays below, which can only say whether a partner contributed
// a bid at all. `state` is undefined while nothing has been fetched yet (shouldn't normally
// happen -- toggleExpanded always kicks off the fetch before this renders -- but rendering
// nothing rather than throwing is the safer default for a lazily-populated map).
function PartnerDiagnostics({ state }) {
  if (!state) return null;
  if (state.loading) return <p className="cell-muted" style={{ margin: '0 0 10px' }}>Loading partner diagnostics…</p>;
  if (state.error) {
    // Expected, not alarming, if migrations/017_auction_partner_results.sql hasn't been applied
    // to production yet -- see that migration's own header and recordPartnerDiagnostics's in
    // auction-service for why a missing table here can never affect the rest of this page.
    return <p className="cell-muted" style={{ margin: '0 0 10px' }}>Partner diagnostics unavailable: {state.error}</p>;
  }
  if (!state.rows || state.rows.length === 0) {
    return <p className="cell-muted" style={{ margin: '0 0 10px' }}>No per-partner diagnostics recorded for this auction.</p>;
  }
  return (
    <table className="data-table" style={{ marginBottom: '10px' }}>
      <thead>
        <tr>
          <th>Partner</th>
          <th>Outcome</th>
          <th>Latency</th>
          <th>HTTP status</th>
        </tr>
      </thead>
      <tbody>
        {state.rows.map((r) => (
          <tr key={r.partner_code}>
            <td><code className="code-chip">{r.partner_code}</code></td>
            <td>
              <span className={`status-badge status-badge--${OUTCOME_BADGE[r.outcome] || 'neutral'}`}>
                {OUTCOME_LABELS[r.outcome] || r.outcome}
              </span>
            </td>
            <td className="cell-muted">{r.latency_ms != null ? `${r.latency_ms} ms` : '—'}</td>
            <td className="cell-muted">{r.http_status != null ? r.http_status : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
